import authFetch from './fetch';
import { getQueryParams, makeRequest } from './utils';
import qs from 'qs';
import { useQuery } from '@tanstack/react-query';

const DISPATCHER_PATH = 'api/Dispatcher';
const DISPATCH_MONITORING_PATH = `${DISPATCHER_PATH}/DispatchMonitoring`;
const DISPATCH_PLANNING_PATH = `${DISPATCHER_PATH}/DispatchPlanning`;

export const getDispatchMonitor = (params) => authFetch
  .get(`${DISPATCH_MONITORING_PATH}${getQueryParams(params)}`)
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const getDispatchPlanner = async (date) => {
  try {
    const response = await authFetch.get(`${DISPATCH_PLANNING_PATH}?Date=${date}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getDispatchPlannerPickups = async (params) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/Pickup${getQueryParams(params)}`),
);

export const getDispatchPlannerReturns = async (params) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/Return${getQueryParams(params)}`),
);

export const getDispatchData = (params) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/All${getQueryParams(params)}`),
);

export const getDispatchBucketsMembersByMarket = (marketId, params) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/BucketEligible/${marketId}${getQueryParams(params)}`),
);

export const getJourneyById = (journeyId) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/Journeys${getQueryParams({ journeyIds: [journeyId] })}`),
);

// Get data by journeys ids
export const getDispatchPlannerByJourneys = (journeyIds, pickup) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/Journeys${getQueryParams({
    journeyIds,
    pickup,
    pageSize: journeyIds.length,
    page: 1,
  })}`),
);

export const getDispatchPlannerDrivers = async (params) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/Drivers${getQueryParams(params)}`),
);

export const dispatchDriversV2 = async (pairings) => makeRequest(
  authFetch.post(`${DISPATCH_PLANNING_PATH}/v2/DispatchDrivers`, pairings),
);

export const getLegacyDispatchPlanner = async (date) => {
  try {
    const response = await authFetch.get(`/api/integration/Dispatcher/DispatchPlanning?Date=${date}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const validateLegacyDriver = async (proNumber, driverId) => {
  try {
    const response = await authFetch.get(
      `/api/integration/Dispatcher/ValidateDriver?Pairing.ProNumber=${proNumber}&Pairing.DriverId=${driverId}`,
    );
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const validateDriver = async (proNumber, driverId) => {
  try {
    const response = await authFetch.get(
      `${DISPATCHER_PATH}/ValidateDriver?Pairing.ProNumber=${proNumber}&Pairing.DriverId=${driverId}`,
    );
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

// pairings = [{proNumber: #, driverId: #}, ...]
export const confirmDrivers = async (pairings) => {
  try {
    const response = await authFetch.post(`${DISPATCHER_PATH}/Confirmation`, { pairings });
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const confirmLegacyDrivers = async (pairings, date) => {
  try {
    const response = await authFetch.post(
      '/api/integration/Dispatcher/Confirmation',
      { pairings, date },
    );
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const dispatchDrivers = async (pairings) => {
  try {
    const response = await authFetch.post(
      `${DISPATCHER_PATH}/DispatchDrivers`,
      { pairings },
    );
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const dispatchLegacyDrivers = async (pairings, date) => {
  try {
    const response = await authFetch.post('/api/integration/Dispatcher/DispatchDrivers', { pairings, date });
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getBobtail = async (id) => {
  try {
    const response = await authFetch.get(`${DISPATCHER_PATH}/GetBobtail?OrderLegIds=${id}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

// data schema: {
//  bobtail: {
//    orderLegId: number,
//    origin: {
//      locationId: number,
//      miles: number
//    },
//    destination: {
//      locationId: number,
//      miles: number
//    },
//    comments: string
//  }

export const postBobtail = async (data) => {
  try {
    const response = await authFetch.post(`${DISPATCHER_PATH}/UpdateBobtail`, data);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getDispatchedDriverStates = async () => {
  try {
    const response = await authFetch.get(`${DISPATCHER_PATH}/DispatchedDriverState`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

// Dispatchers groups
const DISPATCHERS_GROUP_PATH = '/api/DispatcherGroups';

export const getDispatcherGroupsGrid = (params) => makeRequest(
  authFetch.get(`${DISPATCHERS_GROUP_PATH}${getQueryParams(params)}`),
);

export const getDispatcherGroups = (params) => makeRequest(
  authFetch.get(`${DISPATCHERS_GROUP_PATH}/Lookup${getQueryParams(params)}`),
);

export const saveDispatcherGroup = (data) => makeRequest(authFetch.put(DISPATCHERS_GROUP_PATH, data));
export const deleteDispatcherGroup = (id) => makeRequest(authFetch.delete(`${DISPATCHERS_GROUP_PATH}/${id}`));
export const restoreDispatcherGroup = (id) => makeRequest(authFetch.patch(`${DISPATCHERS_GROUP_PATH}/${id}/Restore`));

export const getDispatcherGroupAudits = (id, params) => makeRequest(
  authFetch.get(`${DISPATCHERS_GROUP_PATH}/${id}/Audits${getQueryParams(params)}`),
);

export const getDispatcherGroupAuditProperties = (id) => makeRequest(
  authFetch.get(`${DISPATCHERS_GROUP_PATH}/${id}/Audits/Properties`),
);

export const getDispatcherGroupAuditUsers = (id) => makeRequest(
  authFetch.get(`${DISPATCHERS_GROUP_PATH}/${id}/Audits/Users`),
);

//
const getDispatchersUrl = (params) => `${DISPATCHER_PATH}/Lookup${getQueryParams(params)}`;
export const getDispatchers = (params) => makeRequest(authFetch.get(getDispatchersUrl(params)));
export const useGetDispatchers = (params, opts = {}) => useQuery([getDispatchersUrl(params)], () => getDispatchers(params), opts);

export const getDispatchersAndGroups = (params) => makeRequest(
  authFetch.get(`${DISPATCHER_PATH}/DispatchOrDispatcherGroup/Lookup${getQueryParams(params)}`),
);

export const getDispatchMonitoringPastDue = ({ pickup, marketId }) => makeRequest(
  authFetch.get(`${DISPATCH_MONITORING_PATH}/Buckets/PastDue${getQueryParams({
    pickup,
    marketId,
    sort: 'Customer',
    order: 1,
    page: 1,
    pageSize: 50,
  })}`),
);

export const getChassisUsageReport = async (params) => {
  try {
    const response = await authFetch.get(`${DISPATCHER_PATH}/ChassisUsageReport${getQueryParams(params)}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

// Dispatchers
export const getDispatchersPaginated = async (params) => {
  const query = params && Object.keys(params).length > 0 ? `?${qs.stringify(params, { indices: false })}` : '';
  return makeRequest(authFetch.get(`${DISPATCHER_PATH}${query}`));
};

export const getDispatcher = async (id) => makeRequest(authFetch.get(`${DISPATCHER_PATH}/${id}`));

export const postDispatcher = (data) => makeRequest(authFetch.post(DISPATCHER_PATH, data));

export const deleteDispatcher = async (id) => makeRequest(authFetch.delete(`${DISPATCHER_PATH}/${id}`));

export const restoreDispatcher = async (id) => makeRequest(authFetch.patch(`${DISPATCHER_PATH}/${id}/restore`));

export const getDispatcherPreferences = async () => makeRequest(
  authFetch.get(`${DISPATCHER_PATH}/DispatcherPreferences`),
);

export const getDispatchersAudit = (id, params) => makeRequest(
  authFetch.get(`${DISPATCHER_PATH}/${id}/Audits${getQueryParams(params)}`),
);

export const getDispatchersProperties = (id, params) => makeRequest(
  authFetch.get(`${DISPATCHER_PATH}/${id}/Audits/Properties${getQueryParams(params)}`),
);

export const getDispatchersAuditUsers = (id, params) => makeRequest(
  authFetch.get(`${DISPATCHER_PATH}/${id}/Audits/Users${getQueryParams(params)}`),
);

// Drivers Dispatches Search
export const getDriverDispatches = (params) => makeRequest(
  authFetch.get(`/api/DriverDispatch${getQueryParams(params)}`),
);

// Get Driver Dispatch Message Group
export const getDriverMessageGroup = (driverId) => makeRequest(
  authFetch.get(`${DISPATCHER_PATH}/${driverId}/DriverMessageGroup`),
);

// Dispatch Dashboard: Add Prepull
export const addPrepullToJourney = (journeyId, data) => makeRequest(
  authFetch.post(`${DISPATCH_PLANNING_PATH}/v2/${journeyId}/Prepull`, data),
);

// Dispatch dashboard: Stop off
export const addStopoffToJourney = (journeyId, data) => makeRequest(
  authFetch.post(`${DISPATCH_PLANNING_PATH}/v2/${journeyId}/Stopoff`, data),
);

// Dispatch Dashboard: Add Chassis Split
export const addChassisSplitToJourney = (journeyId, data) => makeRequest(
  authFetch.post(`${DISPATCH_PLANNING_PATH}/v2/${journeyId}/ChassisSplits`, data),
);

// Dispatch Dashboard: Add Rejection
export const addRejection = (journeyId, data) => makeRequest(
  authFetch.post(`${DISPATCH_PLANNING_PATH}/v2/${journeyId}/Rejection`, data),
);

// Dispatch Dashboard: MarkLegDoNotPay
export const markLegDoNotPay = (journeyId, data) => makeRequest(
  authFetch.post(`${DISPATCHER_PATH}/DispatchPlanning/v2/${journeyId}/MarkLegDoNotPay`, data),
);

// Dispatch dashboard settings
export const getDispatchDetailsByMarket = (marketId) => makeRequest(
  authFetch.get(`${DISPATCH_MONITORING_PATH}/MarketViewDetails/${marketId}`),
);

export const saveDispatchDetailsByMarket = (data) => makeRequest(
  authFetch.post(`${DISPATCH_MONITORING_PATH}/MarketViewDetails`, data),
);

// Send to driver
export const sendJourneysToDriver = (journeyIds) => makeRequest(
  authFetch.patch('api/DriverDispatch/sendToDriver', { journeyIds }),
);

// repair orders for journeyId
export const getDispatchRepairOrders = (journeyIds) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/RepairOrders${getQueryParams({ journeyIds })}`),
);
// support requests for journeyId
export const getDispatchSupportRequests = (journeyIds) => makeRequest(
  authFetch.get(`${DISPATCH_PLANNING_PATH}/v2/SupportRequests${getQueryParams({ journeyIds })}`),
);

// Edit journey
export const updateJourney = (journeyId, data) => makeRequest(
  authFetch.patch(`${DISPATCH_PLANNING_PATH}/v2/${journeyId}`, data),
);
